import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, LineChart, ListChecks, Users } from "lucide-react";

import { DashboardPreview } from "@/components/marketing/DashboardPreview";
import { Lede, MarketingFooter, MarketingHeader, Section, SectionHeading } from "@/components/marketing/parts";
import { Button } from "@/components/ui/button";
import { trackCtaClick } from "@/lib/marketing-events";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "MyPriorities — Customers, Revenue & Tasks in One Place" },
      {
        name: "description",
        content:
          "Bring customer management, revenue tracking and task prioritisation into one simple workspace. Built for sole traders and growing teams.",
      },
      {
        property: "og:title",
        content: "MyPriorities — Customers, Revenue & Tasks in One Place",
      },
      {
        property: "og:description",
        content:
          "One workspace for customers, revenue and tasks. Stay organised, protect revenue and focus on the work that matters.",
      },
      { property: "og:url", content: "https://mypriorities.co.uk/" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://mypriorities.co.uk/og-home.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://mypriorities.co.uk/og-home.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://mypriorities.co.uk/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://mypriorities.co.uk/#organisation",
              name: "MyPriorities",
              url: "https://mypriorities.co.uk/",
              logo: "https://mypriorities.co.uk/og-home.jpg",
            },
            {
              "@type": "WebSite",
              "@id": "https://mypriorities.co.uk/#website",
              name: "MyPriorities",
              url: "https://mypriorities.co.uk/",
              publisher: { "@id": "https://mypriorities.co.uk/#organisation" },
            },
            {
              "@type": "SoftwareApplication",
              name: "MyPriorities",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              url: "https://mypriorities.co.uk/",
              description:
                "One workspace for customers, revenue and tasks. Stay organised, protect revenue and focus on the work that matters.",
            },
          ],
        }),
      },
    ],
  }),
  component: Landing,
});

const PILLARS = [
  {
    id: "customer-management",
    icon: Users,
    title: "Customer Management",
    body: "Keep every customer, contact, agreement and important date in one organised place.",
    features: [
      "Customer and contact records",
      "Contracts and key documents",
      "Renewal and review dates",
      "Notes and relationship history",
    ],
    cta: "Explore customer management",
  },
  {
    id: "revenue-management",
    icon: LineChart,
    title: "Revenue Management",
    body: "See what you have contracted, what is coming next and where your revenue needs attention.",
    features: [
      "Contracted revenue",
      "Sales pipeline",
      "Forecasting and deal weighting",
      "Renewals and revenue reporting",
    ],
    cta: "Explore revenue management",
  },
  {
    id: "task-management",
    icon: ListChecks,
    title: "Task Management",
    body: "Turn priorities, customer commitments and commercial opportunities into clear, accountable action.",
    features: [
      "Personal and team tasks",
      "Owners and deadlines",
      "Linked customer and revenue records",
      "Clear priorities and progress",
    ],
    cta: "Explore task management",
  },
];

const FLOW = ["Customer", "Opportunity", "Revenue", "Action"];

function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader signedIn={false} />

      <section className="mx-auto max-w-5xl px-6 pb-16 pt-24">
        <p className="label-caps">One place to run your business</p>
        <h1 className="mt-6 max-w-3xl text-5xl font-bold leading-[1.05] sm:text-6xl">
          Your customers. Your revenue. Your work.{" "}
          <span className="text-primary">Your priorities.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-muted-foreground">
          Keep customers organised, understand where your revenue is coming from and focus your team
          on the work that matters.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          Built for businesses of every size — whether you work alone or lead a team of 60.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Button asChild size="lg">
            <Link to="/auth" onClick={() => trackCtaClick("start_for_free", "hero")}>
              Start for free <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="ghost">
            <Link
              to="/how-it-works"
              onClick={() => trackCtaClick("see_how_it_works", "hero")}
            >
              See how it works
            </Link>
          </Button>
        </div>

        <div className="panel mt-16 overflow-hidden p-2">
          <DashboardPreview />
        </div>

      </section>

      <div className="border-y border-border/60 bg-card/30">
        <Section>
          <SectionHeading>Everything you need to stay in control.</SectionHeading>
          <Lede>
            Manage your customers, protect your revenue and keep the right work moving — without
            spreading your business across disconnected tools.
          </Lede>

          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {PILLARS.map((pillar) => (
              <div key={pillar.id} id={pillar.id} className="panel flex flex-col p-6 scroll-mt-20">
                <pillar.icon className="size-6 text-primary" />
                <h3 className="mt-4 text-lg font-semibold">{pillar.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{pillar.body}</p>
                <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
                  {pillar.features.map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <span className="text-primary">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/business"
                  className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  {pillar.cta} <ArrowRight className="size-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section className="scroll-mt-20">
        <div id="how-it-works" className="scroll-mt-20" />
        <SectionHeading>Connected around the way your business actually works.</SectionHeading>
        <Lede>
          A customer becomes an opportunity. An opportunity becomes revenue. Every commitment
          becomes a task — MyPriorities keeps the whole journey connected.
        </Lede>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          {FLOW.map((step, index) => (
            <div key={step} className="flex items-center gap-3">
              <span className="panel px-4 py-2 text-sm font-medium">{step}</span>
              {index < FLOW.length - 1 ? (
                <ArrowRight className="size-4 text-muted-foreground" />
              ) : null}
            </div>
          ))}
        </div>
      </Section>

      <div className="border-y border-border/60 bg-card/30">
        <Section>
          <SectionHeading>Simple enough for one. Powerful enough for a team.</SectionHeading>
          <Lede>
            Use MyPriorities to organise your own business or give your whole team one shared view of
            customers, revenue and priorities.
          </Lede>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <div className="panel p-6">
              <h3 className="text-lg font-semibold">Sole traders and small businesses</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Replace scattered notes, spreadsheets and task lists with one clear workspace.
              </p>
            </div>
            <div className="panel p-6">
              <h3 className="text-lg font-semibold">Growing teams</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Create shared ownership, consistent processes and visibility across the business.
              </p>
            </div>
          </div>
        </Section>
      </div>

      <Section>
        <SectionHeading>Run your business with clearer priorities.</SectionHeading>
        <Lede>Bring your customers, revenue and work together in MyPriorities.</Lede>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <Link to="/auth" onClick={() => trackCtaClick("start_for_free", "final_cta")}>
              Start for free <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="ghost">
            <Link to="/business" hash="enquiry">
              Book a demo
            </Link>
          </Button>
        </div>
      </Section>

      <MarketingFooter />
    </div>
  );
}
